import { useMemo } from "react";
import { OrderHistory, Unit, useOrderHistory } from "../../../../stores/appOrderHistoryStore";
import { productKey } from "../../../../utils/reports/shared";
import { getProductQuantity } from "../../../../utils/platformUtils";

/** How far back the review flow looks when judging "is this product actually used?". */
export const USAGE_WINDOW_DAYS = 60;

export interface ProductUsage {
    /** Number of orders in the window that contain this product. */
    orderCount: number;
    /** Total units ordered across those orders (raw quantity, per-kg platforms included). */
    totalQuantity: number;
    /**
     * Amount ordered in the product's own standard unit (g / ml / pieces) over the
     * window — `quantity x stdAmount x stdMultiplier` summed, i.e. the "340 kg" the
     * kitchen actually bought rather than a package count. Null when a line carries
     * no usable quantityInfo, or when the lines disagree about the unit: there is no
     * honest total to show then, so the badge falls back to the order count.
     */
    stdQuantity: number | null;
    stdUnit: Unit | null;
    /** Category of the latest line — pieces render as "btl" for wine/beer/water/spirits. */
    category?: string;
    lastOrderDate: Date;
}

export type ProductUsageLookup = (
    platform: string | undefined,
    articleId: string | undefined,
    productName: string | undefined
) => ProductUsage | null;

/**
 * Per-product order frequency over the last {@link USAGE_WINDOW_DAYS} days, built
 * once from the already-loaded order history — no extra request. Used in the
 * automation review as a "how real is this product?" signal: a match against a
 * product nobody has ordered in two months is almost always the wrong product.
 *
 * Keyed with the shared `productKey` primitive so the lookup normalises exactly
 * like every other product aggregation (catch-all ids, no-articleId platforms).
 */
export function useProductUsage(): ProductUsageLookup {
    const orders = useOrderHistory(s => s.orderHistoryList);
    const usageByKey = useMemo(() => buildUsageMap(orders), [orders]);

    return useMemo(() => (platform, articleId, productName) => {
        if (!platform) return null;
        return usageByKey.get(productKey(platform, articleId, productName)) ?? null;
    }, [usageByKey]);
}

// Every ingredient row and every swap candidate calls the hook, so the aggregation
// is cached against the order-history array identity — one pass per data load
// instead of one per mounted row.
let cachedOrders: OrderHistory[] | null = null;
let cachedUsage: Map<string, ProductUsage> | null = null;

function buildUsageMap(orders: OrderHistory[]): Map<string, ProductUsage> {
    if (cachedOrders === orders && cachedUsage) return cachedUsage;

    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - USAGE_WINDOW_DAYS);

    const map = new Map<string, ProductUsage>();
    orders.forEach(order => {
        const orderDate = new Date(order.orderDate);
        if (orderDate < cutoff) return;
        order.productData.forEach(product => {
            const key = productKey(order.platform, product.articleId, product.name);
            const existing = map.get(key);
            const quantity = getProductQuantity(product, order.platform);
            // Per-line amount in std units: the aggregation quantity (rawQuantity on
            // per-kg platforms, via getProductQuantity) times the pack size.
            const qi = product.quantityInfo;
            const perUnit = (qi?.stdAmount ?? 0) * (qi?.stdMultiplier ?? 0);
            const line = qi && perUnit > 0 ? { base: quantity * perUnit, unit: qi.stdUnit } : null;
            if (existing) {
                existing.orderCount += 1;
                existing.totalQuantity += quantity;
                if (!line || existing.stdUnit !== line.unit) {
                    existing.stdQuantity = null;
                    existing.stdUnit = null;
                } else if (existing.stdQuantity !== null) {
                    existing.stdQuantity += line.base;
                }
                if (orderDate > existing.lastOrderDate) {
                    existing.lastOrderDate = orderDate;
                    existing.category = product.category;
                }
            } else {
                map.set(key, {
                    orderCount: 1,
                    totalQuantity: quantity,
                    stdQuantity: line ? line.base : null,
                    stdUnit: line ? line.unit : null,
                    category: product.category,
                    lastOrderDate: orderDate,
                });
            }
        });
    });

    cachedOrders = orders;
    cachedUsage = map;
    return map;
}
